import {
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import { matchesScopeFilter } from '@/pages/recognition/composables/useScopeFilter';

export const BUDGET_KINDS = {
  AWARD: 'award',
  POT: 'pot',
};

export const BUDGET_USAGE_STATES = {
  OK: 'ok',
  LOW: 'low',
  EXHAUSTED: 'exhausted',
};

const BUDGET_USAGE_LOW_THRESHOLD = 75;

const PERIOD_LABELS = {
  WEEK: 'Weekly',
  MONTH: 'Monthly',
  QUARTER: 'Quarterly',
  YEAR: 'Yearly',
};

const PERIOD_NOUNS = {
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
};

export const formatBudgetPeriod = period => PERIOD_LABELS[period] || period || '';

export const budgetPeriodNoun = period => PERIOD_NOUNS[period] || 'period';

export const budgetUsagePercent = ({ amount_minor: amountMinor, spent_minor: spentMinor }) => {
  if (!amountMinor || amountMinor <= 0) {
    return 0;
  }
  return Math.min(((spentMinor || 0) / amountMinor) * 100, 100);
};

export const budgetUsageState = budget => {
  const percent = budgetUsagePercent(budget);
  if (percent >= 100) {
    return BUDGET_USAGE_STATES.EXHAUSTED;
  }
  if (percent >= BUDGET_USAGE_LOW_THRESHOLD) {
    return BUDGET_USAGE_STATES.LOW;
  }
  return BUDGET_USAGE_STATES.OK;
};

export const BUDGET_TYPE_LABELS = {
  [BUDGET_KINDS.AWARD]: 'Award',
  [BUDGET_KINDS.POT]: 'Manager',
};

export const BUDGET_STATUS_FILTERS = {
  ACTIVE: 'active',
  LOW: BUDGET_USAGE_STATES.LOW,
  EXHAUSTED: BUDGET_USAGE_STATES.EXHAUSTED,
  DISABLED: 'disabled',
};

const matchesStatus = (budget, status) => {
  if (status === BUDGET_STATUS_FILTERS.DISABLED) {
    return !budget.is_active;
  }
  if (!budget.is_active) {
    return !status;
  }
  return !status || status === BUDGET_STATUS_FILTERS.ACTIVE || budgetUsageState(budget) === status;
};

const matchesScope = (budget, scope) =>
  matchesScopeFilter(budget.scope, scope, { unscopedMatches: budget.kind === BUDGET_KINDS.AWARD });

export const filterBudgets = (budgets, { type, status, scope } = {}) =>
  (budgets || []).filter(
    budget => (!type || budget.kind === type) && matchesStatus(budget, status) && matchesScope(budget, scope),
  );

export const sortByUsage = budgets =>
  [...(budgets || [])].sort(
    (a, b) => Number(b.is_active) - Number(a.is_active) || budgetUsagePercent(b) - budgetUsagePercent(a),
  );

export const isAwardDisabled = budget => budget.award?.is_active === false;

export const budgetAlert = budgets => {
  const active = (budgets || []).filter(b => b.is_active);
  const exhausted = active.filter(b => budgetUsageState(b) === BUDGET_USAGE_STATES.EXHAUSTED).length;
  const low = active.filter(b => budgetUsageState(b) === BUDGET_USAGE_STATES.LOW).length;
  if (!exhausted && !low) {
    return null;
  }
  return { exhausted, low };
};

const countBudgets = count => `${count} ${count === 1 ? 'budget is' : 'budgets are'}`;

export const budgetAlertMessage = ({ exhausted, low }) => {
  const parts = [];
  if (exhausted) {
    parts.push(`${countBudgets(exhausted)} exhausted`);
  }
  if (low) {
    parts.push(`${countBudgets(low)} running low`);
  }
  return `${parts.join(' and ')}.`;
};

const NEXT_PERIOD_START = {
  WEEK: from => addWeeks(startOfISOWeek(from), 1),
  MONTH: from => addMonths(startOfMonth(from), 1),
  QUARTER: from => addQuarters(startOfQuarter(from), 1),
  YEAR: from => addYears(startOfYear(from), 1),
};

export const nextBudgetPeriodStart = (period, from = new Date()) => NEXT_PERIOD_START[period]?.(from) ?? null;

export const majorToMinor = value => {
  const amount = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
};

export const minorToMajorString = minor => (minor == null ? '' : (minor / 100).toFixed(2));

export const splitManagersByPot = (managerIds, pots, employees) => {
  const potByHolder = new Map((pots || []).map(pot => [pot.holder?.employee_id, pot]));
  const employeeById = new Map((employees || []).map(employee => [employee.employee_id, employee]));
  const withPots = [];
  const withoutPots = [];
  (managerIds || []).forEach(id => {
    if (potByHolder.has(id)) {
      withPots.push(potByHolder.get(id));
    } else {
      withoutPots.push(employeeById.get(id) || { employee_id: id, full_name: id });
    }
  });
  return { withPots, withoutPots };
};

export const legacyAwardBudgets = programs =>
  (programs || [])
    .filter(program => program.budget && !program.budget.recognition_budget_id)
    .map(program => ({
      ...program.budget,
      kind: BUDGET_KINDS.AWARD,
      is_active: program.budget.is_active !== false && program.is_active !== false,
      scope: program.scope || {},
      award: {
        recognition_award_program_id: program.recognition_award_program_id,
        program_name: program.program_name,
        is_active: program.is_active,
      },
    }));

export const noPotsMessage = enforced =>
  enforced
    ? 'These managers have no budget, so they can only give this award without a reward.'
    : "These managers have no budget, so they can still give this award with a reward — a manager budget isn't required yet.";

export const awardsWithoutBudget = programs =>
  (programs || []).filter(
    program =>
      program.is_active &&
      program.program_type === 'PROGRAM' &&
      program.properties?.award_mode !== 'INSTANT' &&
      (program.properties?.award_value_minor || program.properties?.max_award_value_minor) &&
      !program.budget,
  );
